import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 이미 처리했으면 스킵
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    handleAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthCallback = async () => {
    try {
      console.log('[AuthCallback] 시작');
      console.log('[AuthCallback] URL:', window.location.href);
      console.log('[AuthCallback] Hash:', window.location.hash);
      console.log('[AuthCallback] Search:', window.location.search);

      // URL에서 파라미터 확인
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);

      // 에러 체크
      const error_code = queryParams.get('error') || hashParams.get('error');
      const error_description = queryParams.get('error_description') || hashParams.get('error_description');
      if (error_code) {
        console.error('[AuthCallback] OAuth 에러:', error_code, error_description);
        alert(`로그인 오류: ${error_description || error_code}`);
        navigate('/login', { replace: true });
        return;
      }

      // PKCE flow: code 파라미터가 있으면 exchangeCodeForSession 호출
      const code = queryParams.get('code');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      console.log('[AuthCallback] Code:', code ? '존재' : '없음');
      console.log('[AuthCallback] Access token:', accessToken ? '존재' : '없음');
      console.log('[AuthCallback] Refresh token:', refreshToken ? '존재' : '없음');

      let session = null;
      let sessionError = null;

      if (code) {
        // PKCE flow: code를 세션으로 교환
        console.log('[AuthCallback] PKCE flow - code로 세션 교환 시도');
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        session = data.session;
        sessionError = error;
        console.log('[AuthCallback] 세션 교환 결과:', session ? '성공' : '실패', error?.message);
      } else if (accessToken && refreshToken) {
        // Implicit flow: 토큰으로 세션 설정
        console.log('[AuthCallback] 수동 세션 설정 시도');
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        session = data.session;
        sessionError = error;
        console.log('[AuthCallback] 수동 세션 설정 결과:', session ? '성공' : '실패', error?.message);
      } else {
        // 기존 세션 확인
        await new Promise(resolve => setTimeout(resolve, 1000));
        const { data, error } = await supabase.auth.getSession();
        session = data.session;
        sessionError = error;
      }

      console.log('[AuthCallback] 세션:', session ? '존재' : '없음');

      if (sessionError) {
        console.error('[AuthCallback] 세션 오류:', sessionError);
        alert('로그인 중 오류가 발생했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      if (!session) {
        console.error('[AuthCallback] 세션이 없습니다');
        alert('로그인에 실패했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      const user = session.user;
      console.log('[AuthCallback] 사용자 ID:', user.id);

      // SNS ID 생성 (provider_id 조합)
      const provider = user.app_metadata.provider || 'google';
      const snsId = `${provider}_${user.id}`;
      console.log('[AuthCallback] SNS ID:', snsId);

      // DB에서 기존 회원 확인 (대소문자 구분 없이)
      let { data: existingCustomer, error: dbError } = await supabase
        .from('customers')
        .select('*')
        .ilike('sns_id', snsId)
        .maybeSingle(); // single() 대신 maybeSingle() 사용하여 0건일 때 에러 안나게

      console.log('[AuthCallback] 기존 회원:', existingCustomer ? '존재' : '없음');
      console.log('[AuthCallback] DB 에러:', dbError);

      // DB 에러 처리
      if (dbError) {
        // PGRST116: 데이터 없음 (정상) - 신규 회원
        // 406 에러: RLS 정책 문제 - 신규 회원으로 처리
        if (dbError.code === 'PGRST116' ||
            dbError.message?.includes('406') ||
            dbError.code === 'PGRST106') {
          console.warn('[AuthCallback] 기존 회원 없음 또는 RLS 정책 문제 - 신규 회원으로 처리');
          existingCustomer = null; // 명시적으로 null 설정
        } else {
          // 다른 에러는 실제 오류로 처리
          console.error('[AuthCallback] DB 조회 오류:', dbError);
          alert('데이터베이스 오류가 발생했습니다.');
          navigate('/login', { replace: true });
          return;
        }
      }

      // iOS Safari에서 열린 경우 앱으로 리다이렉트 시도
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !navigator.userAgent.includes('CriOS');
      const fragment = window.location.hash;

      if (isIOSSafari && fragment) {
        console.log('[AuthCallback] iOS Safari 감지 - 앱으로 리다이렉트 시도');
        // 앱 스킴으로 리다이렉트 (앱이 설치되어 있으면 앱이 열림)
        window.location.href = `bbibbi://auth/callback${fragment}`;
        // 앱이 열리지 않으면 2초 후 웹에서 계속 진행
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      if (existingCustomer) {
        // 기존 회원 - 바로 로그인
        console.log('[AuthCallback] 기존 회원 로그인 시도');

        // auth_user_id 업데이트 (Google OAuth의 경우 이미 세션이 있음)
        if (!existingCustomer.auth_user_id || existingCustomer.auth_user_id !== user.id) {
          await supabase
            .from('customers')
            .update({ auth_user_id: user.id })
            .eq('sns_id', snsId);
        }

        // AuthContext에 로그인 상태 설정
        await login(snsId);

        console.log('[AuthCallback] 메인으로 이동');
        navigate('/main', { replace: true });
      } else {
        // 신규 회원 - 회원가입 플로우로 이동
        console.log('[AuthCallback] 신규 회원 - 회원가입으로 이동');
        navigate('/signup/phone-verification', {
          replace: true,
          state: {
            snsType: provider,
            snsId: snsId,
            email: user.email || '',
            name: user.user_metadata?.full_name || user.user_metadata?.name || ''
          }
        });
      }
    } catch (error) {
      console.error('[AuthCallback] 처리 오류:', error);
      navigate('/login', { replace: true });
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#1e1e1e',
      color: '#ffffff',
      fontFamily: 'SUIT, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h2>로그인 처리 중...</h2>
        <p>잠시만 기다려주세요.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
